import { useCallback } from 'react';
import { useMasterData } from './useMasterData';
import { createVehicleModelUpdateData } from '../utils/vehicleUtils';

export interface VehicleModelSelectionState {
  vehicleModel: string;
  vehicleType: string;
  vehiclePlateNumber?: string;
  shouldAutoGeneratePlateNumber?: boolean;
}

export interface UseVehicleModelSelectionReturn {
  /**
   * 車両モデル選択変更ハンドラー
   * 車種を自動的に設定します
   */
  handleVehicleModelChange: (vehicleModel: string | null) => void;

  /**
   * 車両モデルIDを直接設定
   */
  setVehicleModelId: (vehicleModel: string) => void;

  /**
   * 車種を直接設定
   */
  setVehicleType: (vehicleType: string) => void;

  /**
   * 現在の状態を取得
   */
  getState: () => VehicleModelSelectionState;

  /**
   * 状態をリセット
   */
  reset: () => void;
}

/**
 * 車両モデル選択とそれに伴う車種自動設定を管理するカスタムフック
 *
 * **ステートレス設計**: このフックは内部状態を持たず、すべての状態は親コンポーネント（formData）で管理します。
 *
 * @param currentVehicleModelId 現在の車両モデルID（formDataから渡される）
 * @param currentVehicleType 現在の車種（formDataから渡される）
 * @param onStateChange 状態変更時のコールバック関数（formData更新用）
 * @returns 車両モデル選択の操作関数
 */
export function useVehicleModelSelection(
  currentVehicleModelId: string = '',
  currentVehicleType: string = '',
  onStateChange?: (state: VehicleModelSelectionState) => void
): UseVehicleModelSelectionReturn {
  const { getVehicleModelById, getVehicleTypeById } = useMasterData();

  /**
   * 車両モデル選択変更ハンドラー
   * 車種を自動的に設定してコールバックを呼び出します
   */
  const handleVehicleModelChange = useCallback(
    (selectedVehicleModelId: string | null) => {
      const updateData = createVehicleModelUpdateData(
        selectedVehicleModelId,
        getVehicleModelById,
        getVehicleTypeById
      );

      onStateChange?.({
        vehicleModel: updateData.vehicleModel,
        vehicleType: updateData.vehicleType,
        vehiclePlateNumber: undefined,
        shouldAutoGeneratePlateNumber: undefined,
      });
    },
    [getVehicleModelById, getVehicleTypeById, onStateChange]
  );

  /**
   * 車両モデルIDを直接設定
   */
  const setVehicleModelId = useCallback(
    (newVehicleModelId: string) => {
      onStateChange?.({
        vehicleModel: newVehicleModelId,
        vehicleType: currentVehicleType,
      });
    },
    [currentVehicleType, onStateChange]
  );

  /**
   * 車種を直接設定
   */
  const setVehicleType = useCallback(
    (newVehicleType: string) => {
      onStateChange?.({
        vehicleModel: currentVehicleModelId,
        vehicleType: newVehicleType,
      });
    },
    [currentVehicleModelId, onStateChange]
  );

  /**
   * 現在の状態を取得
   */
  const getState = useCallback(
    (): VehicleModelSelectionState => ({
      vehicleModel: currentVehicleModelId,
      vehicleType: currentVehicleType,
    }),
    [currentVehicleModelId, currentVehicleType]
  );

  /**
   * 状態をリセット
   */
  const reset = useCallback(() => {
    onStateChange?.({
      vehicleModel: '',
      vehicleType: '',
    });
  }, [onStateChange]);

  return {
    handleVehicleModelChange,
    setVehicleModelId,
    setVehicleType,
    getState,
    reset,
  };
}
